import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Avatar from '@material-ui/core/Avatar';

import joinRequestApi from '../../Api/joinRequestApi';
import GenericState from '../../MyComponents/GenericLoadingErrorState/GenericState';
import store from '../../redux/store';
import { currentRequestCount, updateUserOpenJoinRequests } from '../../redux/actions';
import { GA_EVENT_TREE } from '../../gaConstants';
import { JOIN_REQUEST_STATUS } from '../../shared/JoinRequestStatus';
import useEditStyles from './editStyles';
import { cx } from '../Home/HomeDashboard/dashboardStyles';

const TABS = [
    { status: JOIN_REQUEST_STATUS.OPEN, labelId: 'joinRequest.select.opened' },
    { status: JOIN_REQUEST_STATUS.REJECTED, labelId: 'joinRequest.select.rejected' },
];

const fullName = (row) => `${row.first_name} ${row.last_name}`;

/**
 * The join requests of a group, open or rejected, with accept and reject.
 * Accepting reloads the members list through `onReload`.
 */
const RequestsTable = ({ classId, onReload, requestId }) => {
    const classes = useEditStyles();
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [data, setData] = useState([]);
    const [status, setStatus] = useState(JOIN_REQUEST_STATUS.OPEN);

    const load = (type) => {
        setIsLoading(true);
        return joinRequestApi
            .getJoinRequestsForClass(classId, type)
            .then((res) => {
                setData(res.data);
                setIsLoading(false);
                if (type === JOIN_REQUEST_STATUS.OPEN) {
                    store.dispatch(updateUserOpenJoinRequests(res.data));
                }
            })
            .catch((err) => {
                console.log(err);
                setIsLoading(false);
                setIsError(true);
            });
    };

    useEffect(() => {
        load(status);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classId, status, requestId]);

    const refreshCount = () =>
        joinRequestApi.getCurrentRequestCount().then((res) => store.dispatch(currentRequestCount(res.data.count)));

    const accept = (id) => {
        setIsLoading(true);
        window.logAppActivity(GA_EVENT_TREE.joinBoxActions.actions.accept);
        joinRequestApi
            .acceptJoinRequest(id)
            .then(refreshCount)
            .then(() => load(status))
            .then(onReload);
    };

    const reject = (id) => {
        window.logAppActivity(GA_EVENT_TREE.joinBoxActions.actions.reject);
        setIsLoading(true);
        joinRequestApi
            .rejectJoinRequest(id)
            .then(refreshCount)
            .then(() => load(status));
    };

    return (
        <section className={classes.card}>
            <div className={classes.cardHead}>
                <h2 className={classes.cardTitle}>
                    <FormattedMessage id="redesign.updateClass.openRequests" />
                </h2>
                <div className={classes.tabs} role="tablist">
                    {TABS.map((tab) => (
                        <button
                            key={tab.status}
                            type="button"
                            role="tab"
                            aria-selected={status === tab.status}
                            onClick={() => setStatus(tab.status)}
                            className={cx(classes.tab, status === tab.status && classes.tabOn)}
                        >
                            <FormattedMessage id={tab.labelId} />
                        </button>
                    ))}
                </div>
            </div>

            {isLoading || isError ? (
                <GenericState isLoading={isLoading} isError={isError} inline={true} />
            ) : data.length === 0 ? (
                <div className={classes.empty}>
                    <FormattedMessage
                        id={
                            status === JOIN_REQUEST_STATUS.OPEN
                                ? 'joinRequest.noOpenRequests'
                                : 'redesign.updateClass.requests.emptyRejected'
                        }
                    />
                </div>
            ) : (
                <div className={classes.list} role="table">
                    <div className={cx(classes.grid, classes.head)} role="row">
                        <span />
                        <span className={classes.headCell} role="columnheader">
                            <FormattedMessage id="redesign.generic.label.name.member" />
                        </span>
                        <span />
                        <span />
                    </div>
                    {data.map((row) => {
                        const name = fullName(row);
                        return (
                            <div key={row.id} className={cx(classes.grid, classes.row)} role="row">
                                <Avatar src={row.avatar || undefined} alt="" className={classes.avatar}>
                                    {(row.first_name || '?').charAt(0).toUpperCase()}
                                </Avatar>
                                <div className={classes.main} role="cell">
                                    <p className={classes.name}>{name}</p>
                                </div>
                                <span />
                                <div className={classes.rowActions} role="cell">
                                    <button
                                        type="button"
                                        disabled={
                                            row.status === JOIN_REQUEST_STATUS.ACCEPTED ||
                                            row.status === JOIN_REQUEST_STATUS.CANCELED
                                        }
                                        onClick={() => accept(row.id)}
                                        className={classes.accept}
                                    >
                                        <FormattedMessage id="joinRequest.button.acceptRequest" />
                                    </button>
                                    <button
                                        type="button"
                                        disabled={
                                            row.status === JOIN_REQUEST_STATUS.ACCEPTED ||
                                            row.status === JOIN_REQUEST_STATUS.REJECTED
                                        }
                                        onClick={() => reject(row.id)}
                                        className={classes.reject}
                                    >
                                        <FormattedMessage id="joinRequest.button.rejectRequest" />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </section>
    );
};

export default RequestsTable;
